<template>
  <div class="adult-meal-feedback">
    <!-- La cantine, la date et le service viennent de DashboardContextHeader :
         plus de `userCanteenPreviews[0]`, qui prenait arbitrairement la première
         cantine de l'utilisateur, et aucun sélecteur ad hoc propre à cet écran. -->
    <dashboard-context-header title="MON AVIS" consumer @context-change="onContextChange" />

    <p class="page-subtitle">Qu'avez-vous pensé du repas ?</p>

    <!-- Badge sensibilisation -->
    <div class="eco-badge">
      <div class="eco-icon" aria-hidden="true">🌍</div>
      <div class="eco-text">
        <strong>Sauve la planète !</strong>
        <p>Chaque assiette finie compte</p>
      </div>
    </div>

    <div class="card">
      <meal-feedback-form :canteen-id="canteenId" :service-id="selectedService" :date="selectedDate" @saved="onSaved" />
    </div>

    <!-- Proposition gaspillage, une fois l'avis enregistré -->
    <div v-if="hasSaved && featureWaste" class="waste-promo-card">
      <h2 class="waste-promo-title">👀 Voulez-vous voir combien on a gaspillé aujourd'hui ?</h2>
      <button type="button" ref="wasteTrigger" class="btn-waste" @click="openWaste">🗑️ Voir le gaspillage</button>
    </div>

    <!-- Modal plein écran : gaspillage -->
    <div v-if="showWasteViz" class="fullscreen-modal" role="dialog" aria-modal="true" aria-label="Gaspillage du jour">
      <button type="button" ref="wasteClose" class="fullscreen-close" @click="closeWaste">✕ Fermer</button>
      <waste-input-panel :canteen-id="canteenId" :variant="wasteVariant" />
    </div>
  </div>
</template>

<script>
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import MealFeedbackForm from "@/components/MealFeedbackForm.vue"
import WasteInputPanel from "@/components/WasteInputPanel.vue"

/**
 * Écran de retour repas des adultes sans MOOC (convives titulaires d'un compte et
 * personnels qui déjeunent sur place). Le discriminant est le mode MOOC, pas le rôle :
 * un convive en MOOC est envoyé vers le quiz, hors périmètre de cet écran.
 *
 * Anciennement `ConsumerDashboard2.vue`, une maquette sans appel API. Le formulaire
 * est désormais délégué à `MealFeedbackForm`, partagé avec la card + modale des
 * dashboards du personnel.
 */
export default {
  name: "AdultMealFeedback",

  components: {
    DashboardContextHeader,
    MealFeedbackForm,
    WasteInputPanel,
  },

  data() {
    return {
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      wasteVariant: "barquette",
      featureWaste: false,
      hasSaved: false,
      showWasteViz: false,
    }
  },

  beforeDestroy() {
    this.releaseBodyScroll()
  },

  methods: {
    onContextChange({ canteenId, selectedDate, selectedService, wasteVariant, featureWaste }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
      this.wasteVariant = wasteVariant || "barquette"
      this.featureWaste = Boolean(featureWaste)
      this.hasSaved = false
    },

    onSaved() {
      this.hasSaved = true
    },

    openWaste() {
      this.showWasteViz = true
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = "hidden"
      }
      this.$nextTick(() => {
        if (this.$refs.wasteClose && this.$refs.wasteClose.focus) this.$refs.wasteClose.focus()
      })
    },

    closeWaste() {
      this.showWasteViz = false
      this.releaseBodyScroll()
      // RGAA : le focus revient sur le bouton déclencheur.
      this.$nextTick(() => {
        if (this.$refs.wasteTrigger && this.$refs.wasteTrigger.focus) this.$refs.wasteTrigger.focus()
      })
    },

    releaseBodyScroll() {
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = ""
      }
    },
  },
}
</script>

<style scoped>
.adult-meal-feedback {
  max-width: 640px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.page-subtitle {
  font-size: 1rem;
  color: #4b5563;
  text-align: center;
  margin: 1rem 0 1.5rem;
}

.eco-badge {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 1.5rem;
  color: white;
  margin-bottom: 1.5rem;
}

.eco-icon {
  font-size: 3rem;
}

.eco-text strong {
  display: block;
  font-size: 1.125rem;
  margin-bottom: 0.25rem;
}

.eco-text p {
  font-size: 0.875rem;
  margin: 0;
}

.card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
}

.waste-promo-card {
  background: white;
  border: 2px solid #c2410c;
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
}

.waste-promo-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.btn-waste {
  width: 100%;
  padding: 1rem;
  background: #c2410c;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
}

.btn-waste:hover {
  background: #9a3412;
}

.btn-waste:focus-visible,
.fullscreen-close:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}

.fullscreen-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow-y: auto;
  padding: 1rem;
}

.fullscreen-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: #b91c1c;
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  z-index: 2001;
}

.fullscreen-close:hover {
  background: #991b1b;
}

@media (max-width: 480px) {
  .adult-meal-feedback {
    padding: 1rem;
  }
}
</style>
